/**
 * Service für die Verwaltung von ADNOVA JWT-Authentifizierungstoken
 *
 * Verwaltet automatisches Token-Refresh mit konfigurierbarem Timing, JWT-Validierung
 * und Prüfung auf Token-Ablauf. Fordert neue Token vom Portal über den
 * PortalMessageBroker an, wenn Token ablaufen oder kurz vor Ablauf stehen.
 *
 * @class AdnovaTokenService
 */
import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { NGXLogger } from 'ngx-logger';
import { EMPTY, Observable, of, Subscription, firstValueFrom } from 'rxjs';
import { catchError, filter, switchMap, tap } from 'rxjs/operators';
import { PortalMessageBroker } from './portal-message-broker';

@Injectable({
  providedIn: 'root',
})
export class AdnovaTokenService implements OnDestroy {
  private readonly _logger = inject(NGXLogger);
  private readonly _zone = inject(NgZone);
  private readonly _portalMessageBroker = inject(PortalMessageBroker);

  /**
   * Aktueller ADNOVA JWT Token
   */
  private _token?: string;

  /**
   * Subscription für periodisches Token-Refresh
   */
  private _tokenUpdate?: Subscription;

  /**
   * Erstellt ein periodisches Observable, das in definierten Intervallen emittiert
   *
   * Läuft außerhalb der Angular Zone, um Change Detection nicht zu triggern.
   * Wird für periodische Token-Ablauf-Prüfungen verwendet.
   *
   * @param {number} repeatAfterSeconds Intervall in Sekunden zwischen Emissions
   * @returns {Observable<void>} Observable das periodisch emittiert
   */
  private startPeriodicObservale(repeatAfterSeconds: number): Observable<void> {
    const millisecondsDelayBetweenTokenCheck = repeatAfterSeconds * 1000;
    return new Observable<void>((subscriber) => {
      let intervalId: ReturnType<typeof setInterval> | undefined;
      this._zone.runOutsideAngular(() => {
        intervalId = setInterval(() => this._zone.run(() => subscriber.next()), millisecondsDelayBetweenTokenCheck);
      });
      return () => {
        if (intervalId !== undefined) {
          clearInterval(intervalId);
        }
      };
    });
  }

  /**
   * Prüft ob der ADNOVA JWT abgelaufen ist
   *
   * Betrachtet den Token als abgelaufen, wenn er nicht existiert, leer ist oder
   * seine Ablaufzeit überschritten hat (unter Berücksichtigung eines optionalen Offsets).
   *
   * @param {number} offsetSeconds Optionaler Offset in Sekunden (Standard: 0)
   * @returns {boolean} True wenn Token abgelaufen oder ungültig ist
   */
  private hasAdnovaJwtExpired(offsetSeconds?: number): boolean {
    if (!this._token || this._token.length < 1) {
      return true;
    }

    const decodedToken = jwtDecode<JwtPayload>(this._token);
    return !this.validateTokenNotExpired(decodedToken, offsetSeconds);
  }

  /**
   * Validiert, dass ein JWT-Token noch nicht abgelaufen ist
   *
   * Prüft das 'exp'-Claim des Tokens gegen die aktuelle Zeit mit optionalem Offset.
   * Offset ermöglicht das Erneuern von Token bevor sie tatsächlich ablaufen.
   *
   * @param {JwtPayload} decodedToken Dekodierter JWT Payload
   * @param {number} offsetSeconds Optionaler Zeit-Offset in Sekunden
   * @returns {boolean} True wenn Token noch gültig ist
   */
  private validateTokenNotExpired(decodedToken: JwtPayload, offsetSeconds?: number): boolean {
    offsetSeconds = offsetSeconds || 0;

    // INFO: see TokenHelperService
    // https://github.com/damienbod/angular-auth-oidc-client/blob/version-13/projects/angular-auth-oidc-client/src/lib/utils/tokenHelper/token-helper.service.ts
    const exp = decodedToken.exp;
    if (!exp) {
      return false;
    }
    const tokenExpirationDate = new Date(0); // The 0 here is the key, which sets the date to the epoch
    tokenExpirationDate.setUTCSeconds(exp);

    const tokenExpirationValue = tokenExpirationDate.valueOf();
    const nowWithOffset = new Date(new Date().toUTCString()).valueOf() + offsetSeconds * 1000;
    const tokenNotExpired = tokenExpirationValue > nowWithOffset;

    this._logger.trace(
      `AdnovaTokenService.validateTokenNotExpired(): has adnova token expired: ${!tokenNotExpired}, ${tokenExpirationValue} > ${nowWithOffset}`,
    );
    return tokenNotExpired;
  }

  /**
   * Aktualisiert den internen Token-Speicher
   *
   * @param {string} token Der neue JWT Token
   */
  private updateTokenInternal(token: string): void {
    this._token = token;
    this._logger.debug('AdnovaTokenService.updateTokenInternal(): adnova token updated');
  }

  /**
   * Startet automatischen Token-Refresh-Mechanismus
   *
   * Prüft periodisch Token-Ablauf und fordert neue Token vor Ablauf an.
   * Sollte nur einmal aufgerufen werden. Nachfolgende Aufrufe geben den aktuellen Token zurück.
   *
   * @param {number} repeatAfterSeconds Intervall in Sekunden zwischen Token-Prüfungen
   * @param {number} renewTimeBeforeTokenExpiresInSeconds Zeit vor Ablauf für Erneuerungsanfrage (Standard: 30s)
   * @returns {Promise<string>} Promise das mit initialem Token aufgelöst wird
   */
  public startTokenRefresh(repeatAfterSeconds: number, renewTimeBeforeTokenExpiresInSeconds: number): Promise<string> {
    if (!renewTimeBeforeTokenExpiresInSeconds) {
      renewTimeBeforeTokenExpiresInSeconds = 30;
    }
    if (!this._tokenUpdate) {
      this._tokenUpdate = this.startPeriodicObservale(repeatAfterSeconds)
        .pipe(
          switchMap(() => {
            if (this.hasAdnovaJwtExpired(renewTimeBeforeTokenExpiresInSeconds)) {
              return this._portalMessageBroker.requestAdnovaToken().pipe(
                catchError((err) => {
                  this._logger.error('AdnovaTokenService.startTokenRefresh(): adnova token request failed: ', err);
                  return EMPTY;
                }),
              );
            }
            return of('');
          }),
          filter((value): value is string => {
            return value !== '' && value.length > 0;
          }),
          catchError((err) => {
            this._logger.error('AdnovaTokenService.startTokenRefresh(): periodical adnova token refresh failed: ', err);
            return EMPTY;
          }),
        )
        .subscribe({
          next: (value) => this.updateTokenInternal(value),
          error: (error) =>
            this._logger.error('AdnovaTokenService.startTokenRefresh(): could not refresh adnova token', error),
        });

      this._logger.debug('AdnovaTokenService.startTokenRefresh(): adnova token refresh started');
      return firstValueFrom(
        this._portalMessageBroker.requestAdnovaToken().pipe(
          tap((token) => {
            this.updateTokenInternal(token);
            this._logger.debug('AdnovaTokenService.startTokenRefresh(): initial adnova token arrived');
          }),
        ),
      );
    }
    this._logger.debug('AdnovaTokenService.startTokenRefresh(): adnova token refresh already started, ignoring');
    return firstValueFrom(of(this.getToken()));
  }

  /**
   * Stoppt den automatischen Token-Refresh-Mechanismus
   *
   * Unsubscribed vom periodischen Refresh-Observable und löscht den Token.
   * Wird automatisch bei Service-Destruction aufgerufen.
   */
  public stopTokenRefresh(): void {
    if (this._tokenUpdate) {
      this._tokenUpdate.unsubscribe();
      this._tokenUpdate = undefined;
    }
    this._token = undefined;
  }

  /**
   * Gibt den aktuellen ADNOVA Token zurück
   *
   * @returns {string} Der aktuelle JWT Token
   * @throws {Error} Wenn Token-Refresh nicht läuft oder Token nicht vorhanden ist
   */
  public getToken(): string {
    if (!this._tokenUpdate) {
      throw new Error('token refresh is not running');
    }
    if (!this._token) {
      throw new Error('token not present');
    }
    return this._token;
  }

  /**
   * Angular Lifecycle Hook für Cleanup
   *
   * Stoppt Token-Refresh wenn Service zerstört wird.
   */
  ngOnDestroy(): void {
    this.stopTokenRefresh();
  }
}
